import React, { useEffect } from "react";
import ApiService from "../api/ApiService";
import AppContext from "../context/AppContext";
import { useTranslation } from "react-i18next";

const getStatusColor = (status) => {
  switch (status) {
    case "Scheduled":
      return "bg-primary bg-opacity-25 text-primary";
    case "Ongoing":
      return "bg-warning bg-opacity-25 text-warning";
    case "Complete":
      return "bg-success bg-opacity-25 text-success";
    default:
      return "";
  }
};

const ScheduleCalendar = () => {
  const { t, i18n } = useTranslation();
  const today = new Date();
  const [currentMonth, setCurrentMonth] = React.useState(today.getMonth());
  const [currentYear, setCurrentYear] = React.useState(today.getFullYear());  
  const { user } = React.useContext(AppContext);

  const daysOfWeek =
    i18n.language === "hi"
      ? ["सोम", "मंगल", "बुध", "गुरु", "शुक्र", "शनि", "रवि"]
      : ["MON", "TUE", "WED", "THUR", "FRI", "SAT", "SUN"];

  const handlePrevMonth = () => {
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear(currentYear - 1);
    } else {
      setCurrentMonth(currentMonth - 1);
    }
  };

  const handleNextMonth = () => {
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear(currentYear + 1);
    } else {
      setCurrentMonth(currentMonth + 1);
    }
  };

  const getDaysInMonth = (month, year) => {
    return new Date(year, month + 1, 0).getDate();
  };

  const getFirstDayOffset = (month, year) => {
    const day = new Date(year, month, 1).getDay(); // Sunday = 0
    return (day + 6) % 7; // Convert to Monday = 0
  };

  const totalDays = getDaysInMonth(currentMonth, currentYear);
  const firstDayOffset = getFirstDayOffset(currentMonth, currentYear);

  const prevMonth = currentMonth === 0 ? 11 : currentMonth - 1;
  const prevMonthYear = currentMonth === 0 ? currentYear - 1 : currentYear;
  const prevMonthDays = getDaysInMonth(prevMonth, prevMonthYear);

  // Previous month's trailing days
  const leadingDays = Array.from({ length: firstDayOffset }, (_, i) => ({
    day: prevMonthDays - firstDayOffset + i + 1,
    currentMonth: false,
    month: prevMonth,
    year: prevMonthYear,
  }));

  // Current month days
  const currentDays = Array.from({ length: totalDays }, (_, i) => ({
    day: i + 1,
    currentMonth: true,
    month: currentMonth,
    year: currentYear,
  }));

  // Next month's leading days to fill the last week
  const totalCells = leadingDays.length + currentDays.length;
  const trailingDaysCount = (7 - (totalCells % 7)) % 7;
  const nextMonth = currentMonth === 11 ? 0 : currentMonth + 1;
  const nextMonthYear = currentMonth === 11 ? currentYear + 1 : currentYear;

  const trailingDays = Array.from({ length: trailingDaysCount }, (_, i) => ({
    day: i + 1,
    currentMonth: false,
    month: nextMonth,
    year: nextMonthYear,
  }));

  // Final calendar cells
  const calendarCells = [...leadingDays, ...currentDays, ...trailingDays];

  const rows = [];
  for (let i = 0; i < calendarCells.length; i += 7) {
    rows.push(calendarCells.slice(i, i + 7));
  }

  const [calendarData, setCalendarData] = React.useState({});

  useEffect(() => {
    const fetchSchedules = async () => {
      try {
        const temple_name = user?.temple_name;
        console.log("temple_name", temple_name);

        const response = await ApiService.getUpcomingCalendarSchedules({
          params: { temple_name: temple_name },
        });
        console.log("responseSchedule", response);

        const data = response?.data || [];
        const now = new Date();
        const newCalendarData = {};

        data.forEach((item) => {
          const scheduleDate = new Date(`${item.date}T${item.time}`);

          // Filter by current calendar month & year
          if (
            scheduleDate.getMonth() !== currentMonth ||
            scheduleDate.getFullYear() !== currentYear
          )
            return;

          const day = scheduleDate.getDate();

          const endTime = new Date(scheduleDate.getTime() + 2 * 60 * 60 * 1000); // schedule time + 2 hours

          let statusKey = "Scheduled";

          if (item.is_completed) {
            statusKey = "Complete";
          } else if (now > endTime) {
            statusKey = "Complete";
          } else if (scheduleDate <= now) {
            statusKey = "Ongoing";
          }

          const entry = {
            time: item.time,
            temple: item.temple_name || "",
            status: statusKey, // Keep untranslated status
          };

          if (!newCalendarData[day]) newCalendarData[day] = [];
          newCalendarData[day].push(entry);
        });

        setCalendarData(newCalendarData);
      } catch (err) {
        console.error("Error fetching schedules:", err);
      }
    };

    fetchSchedules();
    const interval = setInterval(fetchSchedules, 30000);
    return () => clearInterval(interval);
  }, [currentMonth, currentYear]);

  const convertTo12HourFormat = (timeStr) => {
    const [hourStr, minute] = timeStr.split(":");
    let hour = parseInt(hourStr, 10);
    const ampm = hour >= 12 ? "PM" : "AM";
    hour = hour % 12 || 12;
    return `${hour.toString().padStart(2, "0")}:${minute} ${ampm}`;
  };

  return (
    <div className="calendar-container w-90% p-5">
      <div
        className="max-w-5xl bg-white mx-auto"
        style={{ paddingBottom: "2%", paddingTop: "1%" }}
      >
        {/* Month navigation */}
        <div
          className="month-nav d-flex justify-content-between align-items-center"
          style={{
            width: "95%",
            justifySelf: "center",
            padding: "5px",
            marginBottom: "1%",
          }}
        >
          <button
            className="month-nav-button btn btn-sm btn-light"
            onClick={handlePrevMonth}
            style={{ width: "4%", color: "#9F1D21" }}
            type="button"
          >
            <span className="fs-4">&lt;</span>
          </button>
          <div
            className="calendar-heading text-center font-semibold text-lg p-2"
            style={{
              backgroundColor: "#EEF3FF",
              width: "90%",
              justifySelf: "anchor-center",
              color: "#43567D",
            }}
          >
            {t("Your Schedule for")}{" "}
            {new Date(currentYear, currentMonth).toLocaleString(
              i18n.language === "hi" ? "hi-IN" : "en-US",
              { month: "long" }
            )}{" "}
            {currentYear}
          </div>{" "}
          <button
            className="month-nav-button btn btn-sm btn-light"
            style={{ width: "4%", color: "#9F1D21" }}
            onClick={handleNextMonth}
            type="button"
          >
            <span className="fs-4">&gt;</span>
          </button>
        </div>

        {/* Calendar Table */}
        <table
          className="calendar-table table-fixed border-collapse bg-white shadow rounded-md mx-auto"
          style={{ width: "95%" }}
        >
          <thead>
            <tr>
              {daysOfWeek.map((day) => (
                <th
                  key={day}
                  className="border p-2 bg-gray-100 text-sm font-medium"
                  style={{ color: "#969696" }}
                >
                  {day}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((week, i) => (
              <tr key={i}>
                {week.map((cell, j) => {
                  const isCurrent = cell.currentMonth;
                  const dateKey = cell.day;
                  const dataEntries = isCurrent ? calendarData[dateKey] : [];

                  return (
                    <td
                      key={j}
                      className="border p-1 text-sm align-top"
                      style={{
                        height: "120px",
                        width: "14.28%",
                        verticalAlign: "top",
                        color: isCurrent ? "#43567D" : "#B0B0B0",
                      }}
                    >
                      <div style={{ height: "100%" }}>
                        <div
                          className="day-number font-semibold mb-1"
                          style={{ textAlign: "left" }}
                        >
                          {cell.day}
                        </div>

                        {dataEntries?.map((entry, index) => (
                          <div
                            key={index}
                            className={`schedule-entry small rounded px-2 py-1 mb-1 ${getStatusColor(
                              entry.status
                            )}`}
                            style={{
                              width: "fit-content",
                              fontSize: "0.75rem",
                              textAlign: "left",
                              marginTop: index === 0 ? "30%" : 0,
                            }}
                          >
                            {convertTo12HourFormat(entry.time)} -{" "}
                            {t(entry.status)}
                          </div>
                        ))}
                      </div>
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ScheduleCalendar;
